import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en/contact' || location.pathname.startsWith('/en/contact/');

  const TEXT = isEnglish ? {
    breadcrumb: { home: 'Home', contact: 'Contact' },
    title: 'Contact',
    formTitle: 'Contact Form',
    nameLabel: 'Name (Optional)',
    namePlaceholder: 'Your name or handle',
    emailLabel: 'Email (Optional)',
    emailPlaceholder: 'example@example.com',
    emailNote: '* Required if you need a reply',
    subjectLabel: 'Subject (Optional)',
    subjectPlaceholder: 'Select a subject',
    subjectOptions: {
      content: 'Question about site content',
      correction: 'Correction or modification request',
      technical: 'Technical question',
      other: 'Other'
    },
    messageLabel: 'Message',
    messagePlaceholder: 'Please write your message (max 500 characters)',
    messageCount: 'characters',
    privacyNote: 'Your message and email address will only be used for replies and necessary communication. See our',
    privacyLink: 'Privacy Policy',
    privacyNoteSuffix: 'for details.',
    submitButton: 'Send',
    submitting: 'Sending...',
    aboutTitle: 'About Contact',
    aboutText: 'Feel free to contact us about:',
    aboutList: [
      'Questions about site content',
      'Correction or deletion requests',
      'Bug reports (broken links, etc.)',
      'Other inquiries'
    ],
    responseTitle: 'Response Time',
    responseList: [
      'We typically respond within 1 week',
      'Some inquiries may take longer',
      'We do not provide individual technical support',
      'We may not respond to commercial inquiries'
    ],
    noticeTitle: 'Notice',
    noticeText: 'This is a personal site. Please understand:',
    noticeList: [
      'We do not provide individual technical guidance or support',
      'We cannot recommend products or provide purchase advice',
      'Please refrain from sales or promotional inquiries'
    ],
    noEmailTitle: 'No Email Address Entered',
    noEmailMessage1: 'You have not entered an email address, so we cannot reply to you.',
    noEmailMessage2: 'Do you still want to send?',
    backButton: 'Back',
    sendAnywayButton: 'Send without email',
    successTitle: 'Thank You for Your Message',
    successMessage1: 'Your message has been received successfully.',
    successMessage2: 'We will review your message and contact you if necessary.',
    successMessage3: 'We typically respond within 1 week. Please wait.',
    submittedInfoTitle: 'Submitted Information',
    submittedName: 'Name:',
    submittedEmail: 'Email:',
    submittedSubject: 'Subject:',
    submittedTime: 'Submitted:',
    homeButton: 'Back to Home',
    backToFormButton: 'Back to Contact',
    errorDefault: 'Failed to send. Please try again.'
  } : {
    breadcrumb: { home: 'ホーム', contact: 'お問い合わせ' },
    title: 'お問い合わせ',
    formTitle: 'お問い合わせフォーム',
    nameLabel: 'お名前（任意）',
    namePlaceholder: 'お名前またはハンドルネーム',
    emailLabel: 'メールアドレス（任意）',
    emailPlaceholder: 'example@example.com',
    emailNote: '※ 返信が必要な場合は必ずご入力ください',
    subjectLabel: 'お問い合わせ種別（任意）',
    subjectPlaceholder: '選択してください',
    subjectOptions: {
      content: 'サイト内容に関する質問',
      correction: '訂正・修正の依頼',
      technical: '技術的な質問',
      other: 'その他'
    },
    messageLabel: 'お問い合わせ内容',
    messagePlaceholder: 'お問い合わせ内容をご記入ください（500文字以内）',
    messageCount: '文字',
    privacyNote: 'お問い合わせ内容およびメールアドレスは、回答および必要な連絡のためだけに利用します。詳しくは',
    privacyLink: 'プライバシーポリシー',
    privacyNoteSuffix: 'をご確認ください。',
    submitButton: '送信する',
    submitting: '送信中...',
    aboutTitle: 'お問い合わせについて',
    aboutText: '以下のような内容について、お気軽にお問い合わせください：',
    aboutList: [
      'サイトの内容に関するご質問',
      '掲載データの訂正や削除のご依頼',
      'リンク切れなどの不具合報告',
      'その他のご連絡'
    ],
    responseTitle: '回答について',
    responseList: [
      '通常、1週間以内に回答いたします',
      '内容によってはお時間をいただく場合があります',
      '技術的な個別サポートは行っておりません',
      '商用利用に関するお問い合わせはお答えできない場合があります'
    ],
    noticeTitle: 'ご注意',
    noticeText: '当サイトは個人運営のため、以下の点にご理解をお願いします：',
    noticeList: [
      '技術的な個別指導やサポートは行っておりません',
      '商品の推奨や購入相談はお答えできません',
      '営業・宣伝目的のお問い合わせはご遠慮ください'
    ],
    noEmailTitle: 'メールアドレスが入力されていません',
    noEmailMessage1: 'メールアドレスが入力されていないため、こちらからの返信ができません。',
    noEmailMessage2: 'それでも送信してよろしいですか?',
    backButton: '戻る',
    sendAnywayButton: 'メールアドレスなしで送信する',
    successTitle: 'お問い合わせありがとうございました',
    successMessage1: '送信いただいた内容は正常に受信しました。',
    successMessage2: '内容を確認のうえ、必要に応じてご連絡させていただきます。',
    successMessage3: '通常、1週間以内に回答いたします。しばらくお待ちください。',
    submittedInfoTitle: '送信内容の確認',
    submittedName: 'お名前:',
    submittedEmail: 'メールアドレス:',
    submittedSubject: '種別:',
    submittedTime: '送信日時:',
    homeButton: 'トップページへ戻る',
    backToFormButton: 'お問い合わせページへ',
    errorDefault: '送信に失敗しました。もう一度お試しください。'
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [confirmNoEmail, setConfirmNoEmail] = useState(false);
  const [pendingValues, setPendingValues] = useState<typeof formData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedData, setSubmittedData] = useState<any>(null);

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: isEnglish ? '/en' : '/' },
    { label: TEXT.breadcrumb.contact, path: isEnglish ? '/en/contact' : '/contact' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitForm = async (values: typeof formData) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const formBody = new URLSearchParams();
      Object.entries(values).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        setSubmitSuccess(true);
        setSubmittedData(result.data);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setConfirmNoEmail(false);
        setPendingValues(null);
      } else {
        setSubmitError(result.message || TEXT.errorDefault);
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError(TEXT.errorDefault);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setPendingValues(formData);
      setConfirmNoEmail(true);
      return;
    }
    
    submitForm(formData);
  };

  const handleConfirmSubmit = () => {
    if (pendingValues) {
      submitForm(pendingValues);
    }
  };

  const handleBackToForm = () => {
    setConfirmNoEmail(false);
    setPendingValues(null);
  };

  const handleBackToFormFromSuccess = () => {
    setSubmitSuccess(false);
    setSubmittedData(null);
  };

  if (submitSuccess) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="metal-panel p-8 md:p-12 text-center">
              <div className="mb-8 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-accent/20 border-2 border-accent flex items-center justify-center">
                  <i className="ri-check-line text-4xl text-accent"></i>
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-white font-['Exo'] mb-6">
                {TEXT.successTitle}
              </h1>

              <div className="section-divider max-w-md mx-auto"></div>

              <div className="space-y-4 text-metal-light mb-8">
                <p className="text-base leading-relaxed">
                  {TEXT.successMessage1}
                </p>
                <p className="text-base leading-relaxed">
                  {TEXT.successMessage2}
                </p>
                <p className="text-sm text-metal-light/80 mt-6">
                  {TEXT.successMessage3}
                </p>
              </div>

              {submittedData && (
                <div className="bg-metal/30 border border-metal rounded-lg p-6 mb-8 text-left">
                  <h3 className="text-white font-semibold mb-4 text-sm">{TEXT.submittedInfoTitle}</h3>
                  <div className="space-y-3 text-sm">
                    {submittedData.name && (
                      <div className="flex items-start">
                        <span className="text-metal-light w-32 flex-shrink-0">{TEXT.submittedName}</span>
                        <span className="text-white">{submittedData.name}</span>
                      </div>
                    )}
                    {submittedData.email && (
                      <div className="flex items-start">
                        <span className="text-metal-light w-32 flex-shrink-0">{TEXT.submittedEmail}</span>
                        <span className="text-white">{submittedData.email}</span>
                      </div>
                    )}
                    {submittedData.subject && (
                      <div className="flex items-start">
                        <span className="text-metal-light w-32 flex-shrink-0">{TEXT.submittedSubject}</span>
                        <span className="text-white">
                          {TEXT.subjectOptions[submittedData.subject as keyof typeof TEXT.subjectOptions] || submittedData.subject}
                        </span>
                      </div>
                    )}
                    <div className="flex items-start">
                      <span className="text-metal-light w-32 flex-shrink-0">{TEXT.submittedTime}</span>
                      <span className="text-white">{submittedData.timestamp}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={isEnglish ? '/en' : '/'} 
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-accent text-black text-sm font-semibold shadow-md hover:bg-accent/80 hover:shadow-lg transition-all transform hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line mr-2"></i>
                  {TEXT.homeButton}
                </a>
                <button 
                  onClick={handleBackToFormFromSuccess}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-metal border border-metal-light text-white text-sm font-semibold shadow-md hover:bg-metal-dark hover:shadow-lg transition-all transform hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  {TEXT.backToFormButton}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (confirmNoEmail) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="metal-panel p-8 text-center">
              <div className="mb-6">
                <i className="ri-mail-close-line text-6xl text-accent"></i>
              </div>
              
              <h1 className="text-3xl font-bold text-white font-['Exo'] mb-6">
                {TEXT.noEmailTitle}
              </h1>
              
              <div className="bg-accent/10 border border-accent/30 p-6 rounded mb-8">
                <p className="text-metal-light text-base leading-relaxed mb-4">
                  {TEXT.noEmailMessage1}
                </p>
                <p className="text-metal-light text-base leading-relaxed">
                  {TEXT.noEmailMessage2}
                </p>
              </div>

              {submitError && (
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded mb-6">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  type="button"
                  onClick={handleBackToForm}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-colors transition-transform hover:-translate-y-0.5 bg-metal/50 text-white border border-metal hover:bg-metal/70 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-2 text-base" aria-hidden="true" />
                  <span>{TEXT.backButton}</span>
                </button>
                
                <button
                  type="button"
                  onClick={handleConfirmSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-colors transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed bg-sky-500 text-black hover:bg-sky-400 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 text-base animate-spin" aria-hidden="true" />
                      <span>{TEXT.submitting}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-2-line mr-2 text-base" aria-hidden="true" />
                      <span>{TEXT.sendAnywayButton}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.title}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="metal-panel p-8">
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.formTitle}
                </h2>

                {submitError && (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 rounded mb-6">
                    <p className="text-red-400 text-sm">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-semibold mb-2 text-sm">
                      {TEXT.nameLabel}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-metal/30 border border-metal text-white rounded focus:border-accent focus:outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder={TEXT.namePlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-semibold mb-2 text-sm">
                      {TEXT.emailLabel}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-metal/30 border border-metal text-white rounded focus:border-accent focus:outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder={TEXT.emailPlaceholder}
                    />
                    <p className="text-metal-light text-xs mt-1">
                      {TEXT.emailNote}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-white font-semibold mb-2 text-sm">
                      {TEXT.subjectLabel}
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-metal/30 border border-metal text-white rounded focus:border-accent focus:outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="">{TEXT.subjectPlaceholder}</option>
                      <option value="content">{TEXT.subjectOptions.content}</option>
                      <option value="correction">{TEXT.subjectOptions.correction}</option>
                      <option value="technical">{TEXT.subjectOptions.technical}</option>
                      <option value="other">{TEXT.subjectOptions.other}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-semibold mb-2 text-sm">
                      {TEXT.messageLabel} <span className="text-accent">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-metal/30 border border-metal text-white rounded focus:border-accent focus:outline-none text-sm resize-vertical disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder={TEXT.messagePlaceholder}
                    />
                    <p className="text-metal-light text-xs mt-1">
                      {formData.message.length}/500{TEXT.messageCount}
                    </p>
                  </div>

                  <div className="bg-accent/10 border border-accent/30 p-4 rounded">
                    <p className="text-accent text-xs leading-relaxed">
                      <i className="ri-information-line mr-2"></i>
                      {TEXT.privacyNote}
                      <a href={isEnglish ? '/en/privacy' : '/privacy'} className="underline hover:no-underline">{TEXT.privacyLink}</a>
                      {TEXT.privacyNoteSuffix}
                    </p>
                  </div>

                  <div className="mt-6 flex justify-center">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-colors transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed bg-sky-500 text-black hover:bg-sky-400 whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line mr-2 text-base animate-spin" aria-hidden="true" />
                          <span>{TEXT.submitting}</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-2-line mr-2 text-base" aria-hidden="true" />
                          <span>{TEXT.submitButton}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              <div className="space-y-6">
                <div className="metal-panel p-6">
                  <h3 className="text-lg font-semibold text-white font-['Exo'] mb-4 flex items-center">
                    <i className="ri-mail-line text-accent mr-2"></i>
                    {TEXT.aboutTitle}
                  </h3>
                  <div className="space-y-3 text-metal-light text-sm">
                    <p>
                      {TEXT.aboutText}
                    </p>
                    <ul className="space-y-2 ml-4">
                      {TEXT.aboutList.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="metal-panel p-6">
                  <h3 className="text-lg font-semibold text-white font-['Exo'] mb-4 flex items-center">
                    <i className="ri-time-line text-accent mr-2"></i>
                    {TEXT.responseTitle}
                  </h3>
                  <div className="space-y-3 text-metal-light text-sm">
                    <ul className="space-y-2">
                      {TEXT.responseList.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="metal-panel p-6">
                  <h3 className="text-lg font-semibold text-white font-['Exo'] mb-4 flex items-center">
                    <i className="ri-alert-line text-accent mr-2"></i>
                    {TEXT.noticeTitle}
                  </h3>
                  <div className="space-y-3 text-metal-light text-sm">
                    <p>
                      {TEXT.noticeText}
                    </p>
                    <ul className="space-y-2 ml-4">
                      {TEXT.noticeList.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ContactPage;
